import React from 'react';
import { gearMain, gearSmall, IMG } from '../mock';
import { Disc3, Sliders } from 'lucide-react';

const HASHTAGS = ['#Professional', '#ReadyToGig', '#DJLife'];
const LIGHTING_TAGS = ['RGB FULL COLOUR', 'DMX READY', '×4 UNITS'];

/* --------------------------------- Subcomponents --------------------------------- */

function SectionHeading() {
  return (
    <div className="lg:sticky lg:top-32 self-start">
      <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-cyan-300/80 uppercase mb-6">
        <span className="h-px w-10 bg-cyan-400/50" />
        <span>// The Setup</span>
        <span className="text-white/25">[section.03]</span>
      </div>
      <h2 className="font-display text-6xl md:text-7xl leading-[0.9] mb-8">
        <span className="block text-white">SOUND</span>
        <span className="block text-white">CHECK</span>
        <span className="block text-gradient-cyan italic">APPROVED</span>
      </h2>
      <div className="font-mono text-[10px] tracking-[0.25em] text-cyan-300/60 mb-3">// gear.manifest</div>
      <p className="text-white/65 text-base leading-relaxed max-w-md mb-8 font-body">
        Anchored by the <span className="text-cyan-200">Pioneer DDJ-FLX10</span> — a flagship 4-channel controller — and the{' '}
        <span className="text-violet-200">Roland SPD-30 Octapad</span> for live percussion. YKAS runs a full pro rig that delivers club-quality sound from rooftops in Bandra to banquet halls across Mumbai.
      </p>
      <div className="flex flex-wrap gap-2 font-mono text-[10px] tracking-[0.2em]">
        {HASHTAGS.map((t) => (
          <span key={t} className="px-2.5 py-1 border border-cyan-400/25 rounded-sm text-cyan-300/70">
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProductBox({ src, alt, color, index }) {
  const isViolet = color === 'violet';
  const bgClass = isViolet
    ? 'bg-gradient-to-br from-violet-800/50 via-violet-900/60 to-[#1a0b2e]'
    : 'bg-gradient-to-br from-cyan-800/40 via-slate-900/60 to-[#0a1620]';
  const glowClass = isViolet
    ? 'bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.4),transparent_65%)]'
    : 'bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.3),transparent_65%)]';
  const borderClass = isViolet ? 'border-violet-300' : 'border-cyan-300';

  return (
    <div className={`relative w-[170px] h-[170px] md:w-[200px] md:h-[200px] rounded-sm overflow-hidden border border-white/10 flex-shrink-0 ${bgClass}`}>
      <div className={`absolute inset-0 pointer-events-none ${glowClass}`} />
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-contain p-2 img-enhance-strong drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
      />
      <div className={`absolute top-1 left-1 w-3 h-3 border-t border-l ${borderClass}`} />
      <div className={`absolute bottom-1 right-1 w-3 h-3 border-b border-r ${borderClass}`} />
      <div className="absolute bottom-1 left-2 font-mono text-[8px] tracking-[0.2em] text-white/80">
        {index}/06
      </div>
    </div>
  );
}

function MainGearCard({ item, index }) {
  const isViolet = item.color === 'violet';
  const cardBg = isViolet
    ? 'border-violet-500/30 bg-gradient-to-br from-violet-950/60 to-[#05030c]'
    : 'border-cyan-500/30 bg-gradient-to-br from-cyan-950/40 to-[#05030c]';
  const glowBg = isViolet ? 'bg-violet-500/20' : 'bg-cyan-500/20';
  const starColor = isViolet ? 'text-violet-300' : 'text-cyan-300';
  const modelColor = isViolet ? 'text-white' : 'text-cyan-100';
  const tagBorder = isViolet ? 'border-violet-400/40 text-violet-200' : 'border-cyan-400/40 text-cyan-200';

  return (
    <div className={`relative rounded-sm border card-glow overflow-hidden ${cardBg}`}>
      <div className={`absolute -right-16 -top-16 w-64 h-64 rounded-full blur-[80px] ${glowBg}`} />
      <div className="relative grid grid-cols-[1fr_170px] md:grid-cols-[1fr_200px] gap-4 p-6 md:p-8">
        <div>
          <div className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] mb-3 ${starColor}`}>
            <span>[★]</span>
            <span>{item.star.toUpperCase()}</span>
          </div>
          <div className="font-mono text-xs tracking-[0.2em] text-white/50 mb-1">{item.brand}</div>
          <h3 className={`font-display text-3xl md:text-4xl leading-tight mb-4 ${modelColor}`}>{item.model}</h3>
          <p className="text-sm text-white/60 mb-1 font-body">{item.desc}</p>
          <p className="text-sm text-white/40 mb-4 font-body">{item.sub}</p>
          <div className="flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <span key={t} className={`font-mono text-[9px] tracking-[0.2em] px-2.5 py-1 rounded-sm border ${tagBorder}`}>
                {t}
              </span>
            ))}
          </div>
        </div>
        <ProductBox src={item.img} alt={item.model} color={item.color} index={`0${index + 1}`} />
      </div>
    </div>
  );
}

function SmallGearCard({ item, index }) {
  return (
    <div className="p-3 rounded-sm border border-violet-500/15 bg-violet-950/10 card-glow overflow-hidden">
      <div className="relative w-full h-32 rounded-sm overflow-hidden mb-3 bg-gradient-to-br from-violet-800/50 via-violet-900/60 to-[#1a0b2e]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35),transparent_65%)] pointer-events-none" />
        <img
          src={item.img}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-contain p-2 img-enhance drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]"
          style={{ objectPosition: item.pos || 'center' }}
        />
        <div className="absolute top-1 right-1 font-mono text-[8px] tracking-[0.2em] text-cyan-300/90 bg-black/60 px-1 rounded-sm z-10">
          0{index + 3}
        </div>
      </div>
      <div className="font-mono text-[9px] tracking-[0.2em] text-cyan-300/70 mb-1">// {item.badge}</div>
      <div className="font-heading text-lg text-white mb-1 tracking-wider">{item.name}</div>
      <div className="text-[10px] text-white/40 leading-tight font-body">{item.desc}</div>
    </div>
  );
}

function LightingCard() {
  return (
    <div className="relative rounded-sm border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/40 to-[#05030c] card-glow overflow-hidden">
      <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-fuchsia-500/15 blur-[80px]" />
      <div className="relative grid grid-cols-[1fr_180px] md:grid-cols-[1fr_220px] gap-4 p-6 items-center">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-fuchsia-300 mb-1">// LIGHTING · ×4 UNITS</div>
          <div className="font-mono text-xs tracking-[0.2em] text-white/50 mb-1">BIG DIPPER LPC007</div>
          <h4 className="font-display text-3xl md:text-4xl text-white mb-2 tracking-wider">PAR LED LIGHTS</h4>
          <p className="text-xs text-white/55 mb-1 font-body">54×3W RGB Full Colour · Fan-cooled · 100,000hr LED lifespan</p>
          <p className="text-xs text-white/40 mb-3 font-body">Sound-reactive · DMX-ready · Transforms any venue into a stage</p>
          <div className="flex flex-wrap gap-2">
            {LIGHTING_TAGS.map((t) => (
              <span key={t} className="font-mono text-[9px] tracking-[0.2em] px-2.5 py-1 rounded-sm border border-fuchsia-400/40 text-fuchsia-200">
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="relative w-[180px] h-[180px] md:w-[220px] md:h-[220px] rounded-sm overflow-hidden border border-white/10 bg-gradient-to-br from-fuchsia-900/40 via-violet-950/60 to-[#1a0b2e] flex-shrink-0">
          <img
            src={IMG.parLights}
            alt="Big Dipper LPC007 PAR LED"
            className="absolute inset-0 w-full h-full object-contain p-2 img-enhance-strong drop-shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
          />
          <div className="absolute top-1 left-1 w-3 h-3 border-t border-l border-fuchsia-300" />
          <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-fuchsia-300" />
          <div className="absolute bottom-1 left-2 font-mono text-[8px] tracking-[0.2em] text-white/80">07/06</div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------- Setup root --------------------------------- */

export default function Setup() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-violet-500/8 blur-[120px]" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-10">
        <SectionHeading />

        <div className="space-y-4">
          {gearMain.map((g, i) => (
            <MainGearCard key={g.model} item={g} index={i} />
          ))}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gearSmall.map((g, i) => (
              <SmallGearCard key={g.name} item={g} index={i} />
            ))}
          </div>

          <LightingCard />
        </div>
      </div>
    </section>
  );
}
